import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useLazyGetBloggerChartStatsQuery } from "../../api";
import type { BloggerListItem } from "../../api/bloggers";
import {
    formatTelegramUsername,
    getBloggerLabel,
    getChartBloggerLabel,
} from "./bloggerLabels";

type ChartMode = "direct" | "total";

interface ChartDatum {
    id: number;
    label: string;
    profileName: string;
    telegram: string | null;
    value: number;
}

const truncateLabel = (value: string): string =>
    value.length > 16 ? `${value.slice(0, 14)}…` : value;

const ChartTooltip: React.FC<{
    active?: boolean;
    payload?: Array<{ payload: ChartDatum }>;
}> = ({ active, payload }) => {
    const { t } = useTranslation("common");
    const datum = payload?.[0]?.payload;

    if (!active || !datum) {
        return null;
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <p className="font-semibold text-gray-900 dark:text-white">
                {datum.label}
            </p>
            {datum.profileName !== datum.label && (
                <p className="text-gray-600 dark:text-gray-300">
                    {datum.profileName}
                </p>
            )}
            {datum.telegram && (
                <p className="text-sky-600 dark:text-sky-400">
                    {datum.telegram}
                </p>
            )}
            <p className="mt-1 text-gray-700 dark:text-gray-200">
                {t("bloggers.chart_invited_value", { count: datum.value })}
            </p>
        </div>
    );
};

interface BloggerStatsModalProps {
    isOpen: boolean;
    onClose: () => void;
    bloggers: BloggerListItem[];
}

const BloggerStatsModal: React.FC<BloggerStatsModalProps> = ({
    isOpen,
    onClose,
    bloggers,
}) => {
    const { t } = useTranslation("common");
    const [mode, setMode] = useState<ChartMode>("direct");
    const [
        loadChartStats,
        {
            data: chartResponse,
            isLoading: isLoadingChart,
            isFetching: isFetchingChart,
            isError: isChartError,
        },
    ] = useLazyGetBloggerChartStatsQuery();

    useEffect(() => {
        if (!isOpen) {
            setMode("direct");
        }
    }, [isOpen]);

    useEffect(() => {
        if (
            isOpen &&
            mode === "total" &&
            !chartResponse &&
            !isLoadingChart &&
            !isChartError
        ) {
            loadChartStats();
        }
    }, [
        chartResponse,
        isLoadingChart,
        isChartError,
        isOpen,
        loadChartStats,
        mode,
    ]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    const chartData = useMemo<ChartDatum[]>(() => {
        const data =
            mode === "direct"
                ? bloggers.map((blogger) => ({
                      id: blogger.id,
                      label: getBloggerLabel(blogger),
                      profileName:
                          [blogger.user.name, blogger.user.surname]
                              .filter(Boolean)
                              .join(" ") || `#${blogger.user.id}`,
                      telegram: formatTelegramUsername(
                          blogger.user.telegram_username
                      ),
                      value: blogger.direct.registered,
                  }))
                : (chartResponse?.data ?? []).map((blogger) => ({
                      id: blogger.id,
                      label: getChartBloggerLabel(blogger),
                      profileName:
                          [blogger.user.name, blogger.user.surname]
                              .filter(Boolean)
                              .join(" ") || `#${blogger.user.id}`,
                      telegram: formatTelegramUsername(
                          blogger.user.telegram_username
                      ),
                      value: blogger.total_invited_count,
                  }));

        return data.sort((first, second) =>
            second.value === first.value
                ? first.label.localeCompare(second.label)
                : second.value - first.value
        );
    }, [bloggers, chartResponse?.data, mode]);

    if (!isOpen) {
        return null;
    }

    const isLoadingTotal =
        mode === "total" && (isLoadingChart || isFetchingChart);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6"
            onMouseDown={(event) => {
                if (event.currentTarget === event.target) {
                    onClose();
                }
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="blogger-chart-title"
                className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-800"
            >
                <div className="flex items-start justify-between border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
                    <div>
                        <h2
                            id="blogger-chart-title"
                            className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white"
                        >
                            <BarChart3 size={22} aria-hidden="true" />
                            {t("bloggers.chart_title")}
                        </h2>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {t("bloggers.chart_caption")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("bloggers.close_chart")}
                        className="rounded-md p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <X size={20} aria-hidden="true" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-4 sm:p-6">
                    <div
                        role="group"
                        aria-label={t("bloggers.chart_scope")}
                        className="mb-5 inline-flex rounded-lg bg-gray-100 p-1 dark:bg-gray-700"
                    >
                        {(["direct", "total"] as ChartMode[]).map((value) => (
                            <button
                                key={value}
                                type="button"
                                aria-pressed={mode === value}
                                onClick={() => setMode(value)}
                                className={`rounded-md px-4 py-2 text-sm font-medium ${
                                    mode === value
                                        ? "bg-white text-blue-700 shadow-sm dark:bg-gray-800 dark:text-blue-300"
                                        : "text-gray-600 dark:text-gray-300"
                                }`}
                            >
                                {t(`bloggers.chart_${value}`)}
                            </button>
                        ))}
                    </div>

                    {isLoadingTotal ? (
                        <div
                            role="status"
                            className="flex h-80 items-center justify-center text-gray-500"
                        >
                            {t("bloggers.loading_chart")}
                        </div>
                    ) : mode === "total" && isChartError ? (
                        <div className="flex h-80 flex-col items-center justify-center gap-3 text-center">
                            <p className="text-red-600">
                                {t("bloggers.chart_error")}
                            </p>
                            <button
                                type="button"
                                onClick={() => loadChartStats()}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                {t("bloggers.retry")}
                            </button>
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="flex h-80 items-center justify-center text-gray-500">
                            {t("bloggers.chart_empty")}
                        </div>
                    ) : (
                        <div className="overflow-x-auto pb-2">
                            <BarChart
                                width={Math.max(680, chartData.length * 88)}
                                height={370}
                                data={chartData}
                                margin={{
                                    top: 16,
                                    right: 24,
                                    left: 8,
                                    bottom: 64,
                                }}
                                accessibilityLayer
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="label"
                                    angle={-35}
                                    textAnchor="end"
                                    height={80}
                                    interval={0}
                                    tickFormatter={truncateLabel}
                                />
                                <YAxis allowDecimals={false} width={64} />
                                <Tooltip
                                    content={
                                        <ChartTooltip />
                                    }
                                />
                                <Bar
                                    dataKey="value"
                                    name={t("bloggers.chart_y_axis")}
                                    fill="#2563eb"
                                    radius={[5, 5, 0, 0]}
                                    maxBarSize={58}
                                />
                            </BarChart>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BloggerStatsModal;
